import React, { useState } from 'react'
import { useI18n } from '../../hooks/useI18n'
import { useCare } from '../../hooks/useCare'
import MonthlyCalendar from '../calendar/MonthlyCalendar'
import ActivityHistory from '../calendar/ActivityHistory'
import DayDetailsModal from '../calendar/DayDetailsModal'

const CalendarPage: React.FC = () => {
  const { t } = useI18n()
  const { currentCase } = useCare()
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'calendar' | 'history'>('calendar')

  if (!currentCase) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">📅</div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            No hay caso seleccionado
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Selecciona un caso para ver el calendario
          </p>
        </div>
      </div>
    )
  }

  const handleDayClick = (date: Date) => {
    setSelectedDate(date)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedDate(null)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t('calendar')}
              </h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Calendario de medicamentos de {currentCase.name}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('calendar')}
              className={`
                flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === 'calendar'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }
              `}
            >
              <span className="text-lg">📅</span>
              <span>Calendario</span>
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`
                flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === 'history'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }
              `}
            >
              <span className="text-lg">📋</span>
              <span>Historial</span>
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-8">
        {activeTab === 'calendar' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 lg:p-6">
            <MonthlyCalendar onDayClick={handleDayClick} />
          </div>
        )}

        {activeTab === 'history' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <ActivityHistory />
          </div>
        )}
      </div>

      {/* Day Details Modal */}
      {isModalOpen && selectedDate && (
        <DayDetailsModal
          selectedDate={selectedDate}
          isOpen={isModalOpen}
          onClose={closeModal}
        />
      )}
    </div>
  )
}

export default CalendarPage